/**
 * React Query key factory — logistics keys (LOC-001, catering, accommodation,
 * et les ressources matérielles de la série RSC).
 *
 * Référence centrale dans `queryKeys.ts` (barrel).
 */

const locationsAll = ['locations'] as const
const cateringAll = ['catering'] as const
const accommodationAll = ['accommodation'] as const
// 30_LOG / RSC — UN SEUL segment, comme ses trois voisines. `['logistics','resources']`
// serait le seul espace à deux segments du dépôt, et le réflexe `['resources']`
// n'y correspondrait à rien.
const resourcesAll = ['resources'] as const
// LOC-014 §10 — le référentiel des motifs d'indisponibilité. SOUS `locations` :
// il ne se gère que depuis le sélecteur de la modale de fermeture (il n'a pas
// d'écran de réglages, règle PO du 2026-09-01), et la bascule d'entité qui purge
// l'espace `locations` doit l'emporter avec elle — les motifs sont PAR ENTITÉ.
const maintenanceReasonsAll = [...locationsAll, 'maintenanceReasons'] as const

export const logisticsKeys = {
    locations: {
        all: locationsAll,
        list: (entityId: string, filters?: object) =>
            [...locationsAll, 'list', entityId, filters] as const,
        detail: (id: string) =>
            [...locationsAll, 'detail', id] as const,
        rooms: (locationId: string) =>
            [...locationsAll, 'rooms', locationId] as const,
        pricing: (locationId: string) =>
            [...locationsAll, 'pricing', locationId] as const,
        nearbyCatering: (locationId: string) =>
            [...locationsAll, 'nearbyCatering', locationId] as const,
        nearbyAccommodation: (locationId: string) =>
            [...locationsAll, 'nearbyAccommodation', locationId] as const,
        searchCatering: (entityId: string, query: string) =>
            [...locationsAll, 'searchCatering', entityId, query] as const,
        searchAccommodation: (entityId: string, query: string) =>
            [...locationsAll, 'searchAccommodation', entityId, query] as const,
        clientSites: (clientId: string) =>
            [...locationsAll, 'clientSites', clientId] as const,
        // LOC-011 — réservation des salles. ⚠️ Le CURSEUR entre dans la clé :
        // sans lui, deux pages différentes partageraient un cache, et la page 2
        // serait remplacée par la page 1 au premier refetch.
        bookings: (locationId: string, filtres?: object | null) =>
            [...locationsAll, 'bookings', locationId, filtres ?? null] as const,
        // 🔴 UNE seule case de paramètres en dernière position, jamais deux.
        // L'invalidation retire la DERNIÈRE case (`cle.slice(0, -1)`) : avec
        // `depuis` et `jusqua` en deux cases, le préfixe gardait un `null` que
        // l'égalité partielle de TanStack comparait à « 2026-09-01 » — le
        // calendrier du lieu n'était JAMAIS invalidé par une écriture, et la
        // bande annulée restait peinte jusqu'au rechargement.
        calendar: (locationId: string, depuis?: string, jusqua?: string) =>
            [
                ...locationsAll, 'calendar', locationId,
                depuis || jusqua ? { from: depuis ?? null, to: jusqua ?? null } : null,
            ] as const,
        // LOC-014 §10 / LOC-010 §4.5 — le COMPTE à venir. La salle, la fenêtre,
        // la réservation écartée et l'entité entrent dans la clé : deux
        // fenêtres différentes partageant un cache feraient dire « 3 réservations
        // tombent dans cette période » sur la période d'à côté.
        upcomingBookings: (locationId: string, params?: object | null) =>
            [...locationsAll, 'upcomingBookings', locationId, params ?? null] as const,
        // LOC-017 — le calendrier CONSOLIDÉ de tous les sites (BOOST).
        roomsPlanning: (params?: object | null) =>
            [...locationsAll, 'roomsPlanning', params ?? null] as const,
        roomsPlanningAggregations: (params?: object | null) =>
            [...locationsAll, 'roomsPlanningAggregations', params ?? null] as const,
        // LOC-007 — vue carte
        map: (entityId: string, query: string) =>
            [...locationsAll, 'map', entityId, query] as const,
        mapCard: (id: string, entityId: string) =>
            [...locationsAll, 'mapCard', id, entityId] as const,
        // LOC-014 §10 — les motifs d'indisponibilité, créés/renommés/masqués
        // depuis le sélecteur lui-même.
        //
        // ⚠️ `entityId` ENTRE dans la clé, comme au CRM : la liste est celle de
        // l'entité qui porte le lieu, et une fiche ouverte depuis une autre
        // entité ne doit pas se voir servir les motifs de la précédente.
        //
        // ⚠️ `includeInactive` aussi : la réponse « avec les masqués » n'est PAS
        // la même liste. Les confondre ferait proposer à la saisie un motif que
        // quelqu'un venait de retirer du sélecteur.
        maintenanceReasons: {
            all: maintenanceReasonsAll,
            list: (entityId: string, search?: string, includeInactive?: boolean) =>
                [
                    ...maintenanceReasonsAll, 'list', entityId,
                    search ?? '', includeInactive ?? false,
                ] as const,
            // Le COMPTE d'indisponibilités qui portent le motif, relu à chaque
            // ouverture de la modale de suppression : c'est l'information de
            // l'instant du DELETE, jamais une valeur de cache.
            usage: (reasonId: string) =>
                [...maintenanceReasonsAll, 'usage', reasonId] as const,
        },
    },
    catering: {
        all: cateringAll,
        list: (entityId: string, filters?: object) =>
            [...cateringAll, 'list', entityId, filters] as const,
        detail: (id: string) =>
            [...cateringAll, 'detail', id] as const,
        menuItems: (providerId: string) =>
            [...cateringAll, 'menuItems', providerId] as const,
        pricing: (providerId: string) =>
            [...cateringAll, 'pricing', providerId] as const,
        // RES-004 — vue carte
        map: (entityId: string, query: string) =>
            [...cateringAll, 'map', entityId, query] as const,
        mapCard: (id: string, entityId: string) =>
            [...cateringAll, 'mapCard', id, entityId] as const,
    },
    accommodation: {
        all: accommodationAll,
        list: (entityId: string, filters?: object) =>
            [...accommodationAll, 'list', entityId, filters] as const,
        detail: (id: string) =>
            [...accommodationAll, 'detail', id] as const,
        roomTypes: (providerId: string) =>
            [...accommodationAll, 'roomTypes', providerId] as const,
        pricing: (providerId: string) =>
            [...accommodationAll, 'pricing', providerId] as const,
        // HEB-003 — vue carte
        map: (entityId: string, query: string) =>
            [...accommodationAll, 'map', entityId, query] as const,
        mapCard: (id: string, entityId: string) =>
            [...accommodationAll, 'mapCard', id, entityId] as const,
    },
    // ===================================================================
    // 30_LOG / RSC — Ressources matérielles.
    // Registre UNION de la série, tenu par RSC-011 §11.6 : aucun ticket ne
    // déclare sa clé ailleurs, chacun renvoie ici. Une clé posée dans son coin
    // produit deux entrées de cache pour une même donnée, dont une que
    // `invalidateQueries` n'atteint jamais — la grille tarifaire et les
    // échéances restaient périmées à l'écran après une sauvegarde continue.
    //
    // FORME UNIQUE, sans exception : ['resources', <verbe>, ...<ids>, <params>].
    // Le verbe d'abord, puis les identifiants, puis les paramètres — sans quoi
    // une invalidation par préfixe ne couvre pas les deux formes.
    //
    // ⚠ Au CHANGEMENT D'ENTITÉ, le socle purge tout l'espace `resourcesAll`, pas
    //   trois clés choisies à la main : un exemplaire, un tarif ou une échéance
    //   affichés après bascule appartiendraient au parc de l'entité précédente.
    //
    // ⚠ Le bloc « Matériel nécessaire » d'une formation (RSC-012) ne pose AUCUNE
    //   clé ici : les siennes vivent dans `queryKeys.catalog.ts`, sous
    //   `catalogKeys.catalog.formationEquipment(productId)` — forme imposée par
    //   ses deux voisines livrées, `formationExpenses` et `formationArticles`.
    // ===================================================================
    resources: {
        all: resourcesAll,
        // LOC-018 §4 — le matériel RANGÉ dans une salle. Sous `resources` et non
        // sous `locations` : c'est une écriture de MATÉRIEL (son rangement) qui
        // doit la périmer, et l'invalidation du parc vise ce préfixe.
        byStorageRoom: (entityId: string, roomId: string) =>
            [...resourcesAll, 'byStorageRoom', entityId, roomId] as const,
        // — socle et liste (RSC-001 / RSC-003)
        list: (entityId: string, params?: unknown) =>
            [...resourcesAll, 'list', entityId, params] as const,
        detail: (id: string) =>
            [...resourcesAll, 'detail', id] as const,
        children: (id: string) =>
            [...resourcesAll, 'children', id] as const,
        // RSC-002 — les consommables liés (mig. 896). L'ENTITÉ entre dans la
        // clé : la lecture est portée par entité, et un même matériel vu depuis
        // deux entités d'un arbre ne doit pas partager une réponse.
        consumables: (id: string, entityId: string) =>
            [...resourcesAll, 'consumables', id, entityId] as const,
        // RSC-003 §5.I — ce que le transfert EMPORTE et ce qu'il DÉTACHE.
        //
        // 🔴 **L'entité d'ARRIVÉE entre dans la clé depuis le 2026-09-17 (A3).**
        // Le commentaire d'ici affirmait l'inverse, et c'était vrai des seules
        // colonnes détachées de la fiche — plus du compte des ÉTIQUETTES
        // perdues, qui dépend de la cible (celle de la racine survit vers une
        // fille `dynamic`, disparaît vers une sœur). Sans elle, le cache
        // servirait à la sœur le « 0 » calculé pour la fille. `null` sans
        // cible : la réponse n'a alors pas de `tags_detached` du tout.
        transferPreview: (id: string, entityId: string, targetEntityId?: string | null) =>
            [...resourcesAll, 'transfer-preview', id, entityId, targetEntityId ?? null] as const,
        // RSC-002 §6.6 — le plan dépend de la famille VISÉE : la mettre dans
        // la clé évite d'afficher les nombres de la famille précédente pendant
        // que la nouvelle réponse arrive.
        familyChangePreview: (id: string, family: string) =>
            [...resourcesAll, 'family-change-preview', id, family] as const,
        typology: (entityId?: string) =>
            [...resourcesAll, 'typology', entityId ?? null] as const,
        // 🔴 **Le PRÉFIXE, et il n'est pas une commodité.** `typology()` sans
        // argument rend `[…, 'typology', null]` — une clé COMPLÈTE, pas un
        // préfixe. Invalider dessus ne touchait donc PAS
        // `[…, 'typology', 'ent_…']`, celle que lisent la fiche et le sélecteur
        // de type. Avec `staleTime: 30 min`, un champ personnalisé créé au
        // référentiel n'apparaissait dans l'onglet Caractéristiques qu'une
        // demi-heure plus tard, ou après un rechargement forcé (PO,
        // 2026-09-09). Le défaut est INVISIBLE à la relecture : l'appel
        // `invalidateQueries({ queryKey: queryKeys.resources.typology() })` a
        // l'air exactement juste.
        typologyAll: () => [...resourcesAll, 'typology'] as const,
        // RSC-002 §10 — l'administration du référentiel. UN segment commun aux
        // trois tables : modifier l'une change ce que `typology` sert, et les
        // invalider séparément laisserait le sélecteur de type sur l'ancien
        // libellé jusqu'au rechargement.
        referentiel: () =>
            [...resourcesAll, 'referentiel'] as const,
        // 36_CUSTOM / CUSTOM-003 — les définitions de champs que lit la liste du parc (colonnes
        // `attr_`). ⛔ SOUS `referentiel()` : invalider le référentiel les relit aussi, et
        // l'écriture d'une définition les vise par cette fabrique, jamais par un segment écrit.
        referentielAttributes: () =>
            [...resourcesAll, 'referentiel', 'attributes'] as const,
        // ⛔ Ni `facets` ni `aggregations` ici : ces deux routes sont consommées
        // par le socle DataTable sous SES clés (`['datatable-facets', …]`,
        // `['datatable-aggregations', …]`), et le seul chemin qui les rafraîchit
        // est `invalidateDataTableListings()` (`lib/datatable-invalidate.ts`).
        // Une clé posée ici n'aurait AUCUN lecteur : elle ferait croire qu'on
        // sait invalider les facettes, et une invalidation écrite dessus ne
        // rafraîchirait rien — un menu de filtre figé, sans erreur. Retirées le
        // 2026-09-10 après l'audit des experts ; ne pas les remettre.
        // — exemplaires (RSC-001, consommés par RSC-003 / RSC-006 / RSC-009)
        units: (id: string, params?: unknown) =>
            [...resourcesAll, 'units', id, params] as const,
        unitsDistribution: (id: string) =>
            [...resourcesAll, 'unitsDistribution', id] as const,
        // Les facettes de la liste d'exemplaires. Les paramètres ENTRENT dans
        // la clé (recherche, exemplaires sortis du parc) : ils changent les
        // compteurs. L'entité, non — le socle purge tout `resourcesAll` à la
        // bascule, la remettre ici doublerait la même purge.
        unitsFacets: (id: string, params?: unknown) =>
            [...resourcesAll, 'unitsFacets', id, params] as const,
        trackingModePreview: (id: string) =>
            [...resourcesAll, 'trackingModePreview', id] as const,
        // — profils de la typologie et leurs valeurs (RSC-002 / RSC-004a).
        // UNE clé pour les deux : l'onglet « Caractéristiques » les affiche d'un
        // bloc, et deux clés feraient deux allers-retours au montage.
        profiles: (id: string) =>
            [...resourcesAll, 'profiles', id] as const,
        // — catalogue des catégories réglementaires (RSC-005 §8). Une CONSTANTE
        // du serveur, pas une donnée : sans paramètre, et relue au déploiement.
        axesCatalog: () =>
            [...resourcesAll, 'axesCatalog'] as const,
        // — conformité et alertes (RSC-006 / RSC-007)
        controls: (id: string) =>
            [...resourcesAll, 'controls', id] as const,
        controlTypes: () =>
            [...resourcesAll, 'controlTypes'] as const,
        controlTypeAlerts: (controlTypeId: string) =>
            [...resourcesAll, 'controlTypeAlerts', controlTypeId] as const,
        // ⛔ Pas de `compliance` ici : la vue « Vérifications » vit sous sa clé
        // BRUTE `['/api/logistics/compliance/due', params]`, celle du socle
        // DataTable. Ce verbe n'avait que deux appelants, tous deux des
        // invalidations qui ne rafraîchissaient rien — aucun `useQuery` ne
        // lisait cette clé. Retiré le 2026-09-10 ; ne pas le remettre.
        alerts: (id: string) =>
            [...resourcesAll, 'alerts', id] as const,
        // RSC-006 §7 — les relevés de compteur (km, heures). Le verbe d'abord :
        // rangés sous `detail(id)`, ils vivaient SOUS un identifiant, contre la
        // forme imposée plus haut.
        usageReadings: (id: string) =>
            [...resourcesAll, 'usageReadings', id] as const,
        // RSC-006 §8 — les réserves d'un passage. `openOnly` est un PARAMÈTRE et
        // se met en queue : les deux vues (toutes / seulement ouvertes) sont deux
        // réponses différentes, les confondre montrerait des réserves soldées.
        reserves: (id: string, entityId: string, openOnly?: boolean) =>
            [...resourcesAll, 'reserves', id, entityId, openOnly ?? false] as const,
        // — réservation et planning (RSC-009)
        bookings: (resourceId: string, params?: unknown) =>
            [...resourcesAll, 'bookings', resourceId, params] as const,
        calendar: (resourceId: string, params?: unknown) =>
            [...resourcesAll, 'calendar', resourceId, params] as const,
        availability: (resourceId: string, params?: unknown) =>
            [...resourcesAll, 'availability', resourceId, params] as const,
        planning: (params?: unknown) =>
            [...resourcesAll, 'planning', params] as const,
        // RSC-009 — ce qui est réservé DEVANT nous, la question posée au moment
        // de sortir un matériel du parc. L'exemplaire entre dans la clé et vaut
        // `null` pour la ressource entière : sans lui, la réponse du matériel
        // complet s'afficherait pour l'exemplaire n° 7.
        upcomingBookings: (id: string, resourceUnitId?: string | null) =>
            [...resourcesAll, 'upcomingBookings', id, resourceUnitId ?? null] as const,
        // — matériel loué (RSC-008)
        supply: (id: string) =>
            [...resourcesAll, 'supply', id] as const,
        pricing: (id: string) =>
            [...resourcesAll, 'pricing', id] as const,
        costEstimate: (id: string, params?: unknown) =>
            [...resourcesAll, 'costEstimate', id, params] as const,
        // — engagement (RSC-010)
        engagementChecks: (id: string) =>
            [...resourcesAll, 'engagementChecks', id] as const,
        // RSC-010 §5.7 — le brouillon de demande de commande chez le loueur. La
        // réservation entre dans la clé : le texte est COMPOSÉ à partir d'elle,
        // et deux réservations du même matériel ne donnent pas le même message.
        orderRequest: (id: string, bookingId: string | null) =>
            [...resourcesAll, 'orderRequest', id, bookingId] as const,
    },
}
